import React, { useEffect, useRef, useState } from 'react';

// Reveals its children with a fade-up once they scroll into view.
// `as` picks the wrapper element, `delayIndex` staggers siblings.
export default function Reveal({
  as: Tag = 'div',
  className = '',
  delayIndex,
  children,
  ...rest
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect reduced motion — show immediately.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return;
    }

    // If IntersectionObserver isn't available, just show it.
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    io.observe(el);

    // Safety net: if the element is already at/above the viewport on mount
    // (e.g. a deep-link anchor jump), reveal it so content never gets stuck
    // hidden waiting for an intersection that already happened.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight) setVisible(true);

    return () => io.disconnect();
  }, []);

  const style = delayIndex != null ? { '--i': delayIndex, ...rest.style } : rest.style;

  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'is-visible' : ''} ${className}`.trim()}
      {...rest}
      style={style}
    >
      {children}
    </Tag>
  );
}
